<template>
  <div>
    <Modal modalId="cancel-subscription-modal" size="modal-md" @hidden="closeManagePlanModal()">
      <ModalHeader
        :title="$t('xCancelPlan')"
        icon="far fa-circle-dollar colorOrange mr-1"
        :style="{ padding: '4px 12px' }"
      >
        <div class="cols-auto">
          <button
            type="button"
            class="btn btn-icon-button btn-icon-button--gray"
            @click="closeManagePlanModal()"
            data-test-hook="CloseModalBtn"
          >
            <i class="fas fa-times" />
          </button>
        </div>
      </ModalHeader>
      <ModalBody>
        <div class="row mb-3">
          <div class="col">
            <p data-test-hook="EndOfSubscriptionText">
              {{ $t('xCurrentPlanDateText') }}
              {{ $moment(localEndDate ?? endDate).format('MM/DD/YYYY') + '.' }}
              {{ $t('xCurrentPlanText') }}
            </p>
          </div>
        </div>
        <div v-if="doesAccountHaveGen() && !doesAccountHaveFuelMonitor()">
          <div class="row mb-3">
            <div class="col">
              <span class="text" data-test-hook="BasicFeaturesTextGenOnly">
                {{ $t('xGenToReceiveBasicFeaturesText') }}
              </span>
            </div>
          </div>
          <strong>{{ $t('xPremiumGenFeaturesHeader') }}</strong>
          <ul>
            <li>{{ $t('xGenPremiumFeaturesItem1') }}</li>
            <li>{{ $t('xGenPremiumFeaturesItem2') }}</li>
            <li>{{ $t('xGenPremiumFeaturesItem3') }}</li>
            <li>{{ $t('xGenPremiumFeaturesItem4') }}</li>
          </ul>
        </div>
        <div v-else-if="!doesAccountHaveGen() && doesAccountHaveFuelMonitor()">
          <div class="row mb-3">
            <div class="col">
              <span class="text" data-test-hook="BasicFeaturesTextMonitorOnly">
                {{ $t('xMonitorToReceiveBasicFeaturesText') }}
              </span>
            </div>
          </div>
          <strong>{{ $t('xPremiumFuelMonitorFeaturesHeader') }}</strong>
          <ul>
            <li>{{ $t('xMonitorPremiumFeaturesItem1') }}</li>
            <li>{{ $t('xMonitorPremiumFeaturesItem2') }}</li>
            <li>{{ $t('xMonitorPremiumFeaturesItem3') }}</li>
            <li>{{ $t('xMonitorPremiumFeaturesItem4') }}</li>
          </ul>
        </div>
        <div v-else>
          <div class="row mb-3">
            <div class="col">
              <span class="text" data-test-hook="BasicFeaturesTextGenAndMonitor">
                {{ $t('xGenAndMonitorBasicFeaturesText') }}
              </span>
            </div>
          </div>
          <strong>{{ $t('xPremiumGenFeaturesHeader') }}</strong>
          <ul>
            <li>{{ $t('xGenPremiumFeaturesItem1') }}</li>
            <li>{{ $t('xGenPremiumFeaturesItem2') }}</li>
            <li>{{ $t('xGenPremiumFeaturesItem3') }}</li>
            <li>{{ $t('xGenPremiumFeaturesItem4') }}</li>
          </ul>
          <strong>{{ $t('xPremiumFuelMonitorFeaturesHeader') }}</strong>
          <ul>
            <li>{{ $t('xMonitorPremiumFeaturesItem1') }}</li>
            <li>{{ $t('xMonitorPremiumFeaturesItem2') }}</li>
            <li>{{ $t('xMonitorPremiumFeaturesItem3') }}</li>
            <li>{{ $t('xMonitorPremiumFeaturesItem4') }}</li>
          </ul>
        </div>
        <div class="row" data-test-hook="AreYouSureYouWantToCancelText">
          <div class="col">
            {{ $t('xAreYouSureYouWantToCancelText') }}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div class="modal-buttons">
          <button
            type="button"
            class="btn btn-outline-warning"
            @click="closeManagePlanModal()"
            :title="$t('xManageYourPlan')"
            data-test-hook="KeepPlanBtn"
          >
            {{ $t('xKeepPlanButtonText') }}
          </button>
          <button
            type="button"
            class="btn btn-warning ml-2"
            @click="cancelSubscription()"
            :title="$t('xManageYourPlan')"
            data-test-hook="CancelPlanBtn"
          >
            {{ $t('xCancelPlanButtonText') }}
          </button>
        </div>
      </ModalFooter>
    </Modal>

    <Modal modalId="cancel-subscription-spinner-modal" size="modal-lg">
      <ModalBody style="padding-top: 100px; padding-bottom: 100px;">
        <div class="d-flex justify-content-center align-items-center">
          <b-spinner style="width: 10rem; height: 10rem;" variant="warning"></b-spinner>
        </div>
      </ModalBody>
    </Modal>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import * as Constants from '@/constants'
import { openModal, closeModal, pollForSubmissionUpdate, calculateSubscriptionEndDate } from '@/utils/mixins'

export default {
  name: 'CancelSubscriptionModal',
  data() {
    return {
      accountData: null,
      subscriptionDetails: null,
      localEndDate: null
    }
  },
  props: [
    'endDate',
    'isCancelLegacy',
    'cancelLegacyApparatusId',
    'cancelLegacyApparatusType',
    'cancelSubscriptionApparatuses',
    'cancelSubscriptionProductId',
    'cancelSubscriptionStore'
  ],
  computed: {
    ...mapState({
      accountProfile: state => state.account.accountProfile
    })
  },
  inject: ['updateSubscriptionDetails'],
  methods: {
    closeManagePlanModal() {
      this.closeModal('cancel-subscription-modal')
    },
    cancelSubscription() {
      this.closeManagePlanModal()
      this.openModal('cancel-subscription-spinner-modal')
      if (this.isCancelLegacy) {
        this.cancelLegacySubscription(this.accountData.organizationId, this.cancelLegacyApparatusId)
      } else {
        this.cancelSubscriptionProduct(
          this.accountData.organizationId,
          this.cancelSubscriptionStore,
          this.cancelSubscriptionProductId
        )
      }
    },
    async cancelLegacySubscription(orgId, apparatusId) {
      await this.$store
        .dispatch('cancelApparatusSubscription', {
          organizationId: orgId,
          apparatusId: apparatusId
        })
        .then(() => {
          this.closeModal('cancel-subscription-spinner-modal')
          this.pollForSubmissionUpdate(
            'getSubscriptionDetails',
            this.accountProfile.organizationId,
            subscriptionData => {
              const apparatusSubscription = subscriptionData?.legacyBillingProducts?.products?.find(
                product => product?.apparatusId === apparatusId
              )

              return (
                apparatusSubscription?.status === Constants.SubscriptionCancelled ||
                apparatusSubscription?.status === Constants.SubscriptionPendingCancellation
              )
            },
            'md'
          )
            .then(newSubscriptionData => {
              // Show the green toast if the API has acknowledged our change (either immediately applied or pending).
              if (newSubscriptionData) {
                // Clear any existing pending changes because a cancel will overwrite anything pending.
                this.clearBlueSubscriptionPendingBanner()

                this.updateSubscriptionDetails(newSubscriptionData)
                this.showSuccessToast()
              }
              // Otherwise, processing is not complete yet, show the blue banners.
              else {
                this.showBlueSubscriptionPendingBanner()
              }
            })
            .catch(() => {
              this.showSomethingWentWrongErrorToast()
            })
        })
        .catch(() => {
          this.closeModal('cancel-subscription-spinner-modal')
          this.showCancellationFailedErrorToast()
        })
    },
    async cancelSubscriptionProduct(orgId, store, productId) {
      await this.$store
        .dispatch('cancelSubscriptionProduct', {
          organizationId: orgId,
          store: store,
          productId: productId
        })
        .then(() => {
          this.closeModal('cancel-subscription-spinner-modal')
          this.pollForSubmissionUpdate(
            'getSubscriptionDetails',
            this.accountProfile.organizationId,
            subscriptionData => {
              const cancelledSubscription = subscriptionData?.subscriptions?.find(
                sub => sub?.store === store && sub?.product?.productId === productId
              )

              return (
                cancelledSubscription?.status === Constants.SubscriptionDetailPendingCancel ||
                cancelledSubscription?.status === Constants.SubscriptionDetailCanceled
              )
            },
            'md'
          )
            .then(newSubscriptionData => {
              // Show the green toast if the API has acknowledged our change (either immediately applied or pending).
              if (newSubscriptionData) {
                // Clear any existing pending changes because a cancel will overwrite anything pending.
                this.clearBlueSubscriptionPendingBanner()

                this.updateSubscriptionDetails(newSubscriptionData)
                this.showSuccessToast()
              }
              // Otherwise, processing is not complete yet, show the blue banners.
              else {
                this.showBlueSubscriptionPendingBanner()
              }
            })
            .catch(() => {
              this.showSomethingWentWrongErrorToast()
            })
        })
        .catch(() => {
          this.closeModal('cancel-subscription-spinner-modal')
          this.showCancellationFailedErrorToast()
        })
    },
    doesAccountHaveGen() {
      return (
        (this.isCancelLegacy && this.cancelLegacyApparatusType === Constants.HsbApparatusType) ||
        (!this.isCancelLegacy &&
          this.cancelSubscriptionApparatuses?.find(apparatus => apparatus.type === Constants.HsbApparatusType))
      )
    },
    doesAccountHaveFuelMonitor() {
      return (
        (this.isCancelLegacy && this.cancelLegacyApparatusType === Constants.FuelTankApparatusType) ||
        (!this.isCancelLegacy &&
          this.cancelSubscriptionApparatuses?.find(apparatus => apparatus.type === Constants.FuelTankApparatusType))
      )
    },
    /** Shows generic red error toast. */
    showSomethingWentWrongErrorToast() {
      this.$store.dispatch('displayToast', {
        title: this.$t('xSomethingWentWrong'),
        message: this.$t('xWaitAndTryAgainErrorText'),
        variant: 'danger'
      })
    },
    /** Shows red error toast for failure to cancel subscription. */
    showCancellationFailedErrorToast() {
      this.$store.dispatch('displayToast', {
        title: this.$t('xError'),
        message: this.$t('xFailedToCancelDeviceSubscription'),
        variant: 'danger'
      })
    },
    /** Shows green toast when plan update is successful. */
    showSuccessToast() {
      this.$store.dispatch('displayToast', {
        title: this.$t('xSuccess'),
        message: this.$t('xPlanCanceled'),
        variant: 'success'
      })
    },
    /** Removes all pending subscription changes that would have triggered the blue banner to show. */
    clearBlueSubscriptionPendingBanner() {
      this.$store.dispatch('deletePendingSubscriptionChanges')
    },
    /** Update global state to include pending subscriptions change info regarding this cancellation. */
    showBlueSubscriptionPendingBanner() {
      const pendingChange = {
        apparatusIds: [...this.cancelSubscriptionApparatuses.map(apparatus => apparatus.apparatusId)],
        // Not needed for cancel.
        pendingProductId: '',
        isPendingCancel: true
      }
      this.$store.dispatch('addPendingSubscriptionChanges', pendingChange)
    },
    openModal,
    closeModal,
    pollForSubmissionUpdate,
    calculateSubscriptionEndDate
  },
  async mounted() {
    if (this.accountProfile) {
      this.accountData = _.cloneDeep(this.accountProfile)
      await this.$store
        .dispatch('getSubscriptionDetails', this.accountProfile.organizationId)
        .then(response => {
          this.subscriptionDetails = response.data
          const purchaseDate = this.subscriptionDetails?.subscriptions[0]?.purchasedDate
          const duration = this.subscriptionDetails?.subscriptions[0]?.product?.durationInMonths
          if (purchaseDate && duration) {
            this.localEndDate = this.calculateSubscriptionEndDate(purchaseDate, duration)
          } else {
            this.localEndDate = null
          }
        })
        .catch(() => {})
    } else {
      this.$store.dispatch('getAccountProfile')
    }
  }
}
</script>
